import { defineComponent as _defineComponent } from 'vue'
import Modal from "../Modal.vue";
  import { computed, onBeforeUnmount, reactive, ref, watch } from "vue";
  import CheckboxInput from "../input/CheckboxInput.vue";
  import useVuelidate from "@vuelidate/core";
  import {
    required,
    helpers,
    minLength,
    maxLength,
  } from "@vuelidate/validators";
  import Cleave from "vue-cleave-component";
  import { phoneNumberOption, phonePlaceholder } from "@/helpers/cleavePlugin";
  import { validPhoneNumber } from "@/helpers/validation";
  import store from "@/store";
  import { useToast, POSITION } from "vue-toastification";
  import KlaviyoErrorEnum from "@/enums/KlaviyoEnum";
  import axios from "axios";
  import ErrorMessages from "@/constants/errorsMessages";
  import { LeadForVerificationPhoneNumber } from "@/types/LeadInterface";
  import parseAustralianNumberToInternational from "@/helpers/phone-number";
  import {
    ValidatePhoneNumberDto,
    ResendCodeDto,
  } from "@/types/KlaviyoInterface";

  const SET_INITIAL_COUNTDOWN_SECONDS = 40;
  
export default /*@__PURE__*/_defineComponent({
  __name: 'PhoneNumberVerificationModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    quote: {
      type: Object,
      required: true,
    },
  },
  emits: ["close", "back", "send"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  const toast = useToast();

  const emits = __emit;

  const props = __props;

  const rules = {
    phone: {
      required,
      validPhoneNumber: helpers.withMessage(
        "Value must be valid phone number",
        validPhoneNumber
      ),
    },
    digitCode: {
      required: helpers.withMessage(
        "Please enter the security code we just sent you via SMS.",
        required
      ),
      minLength: helpers.withMessage(
        "The security code must be exactly 6 digits.",
        minLength(6)
      ),
      maxLength: helpers.withMessage(
        "The security code must be exactly 6 digits.",
        maxLength(6)
      ),
    },
  };

  const formFields = {
    email: props.quote?.lead?.phone,
    phone: props.quote?.lead?.email,
    digitCode: "",
  };
  const form = reactive(Object.assign({}, formFields));
  const v$ = useVuelidate(rules, form);
  const isLoading = ref(false);
  const isButtonDisabled = ref(false);
  const buttonDisabledInterval = ref<number | null>(null);
  const countdown = ref(SET_INITIAL_COUNTDOWN_SECONDS);

  const resendButtonText = computed(() => {
    return isButtonDisabled.value
      ? `Resend Code after(${countdown.value}s)`
      : "Resend Code";
  });

  const onClose = () => {
    if (!isLoading.value) {
      form.digitCode = "";
      emits("back");
    }
  };

  const onResendCode = async () => {
    if (isButtonDisabled.value || isLoading.value) return;

    const normalizedPhone = parseAustralianNumberToInternational(form.phone);

    try {
      const params: ResendCodeDto = {
        email: form.email,
        phoneNumber: normalizedPhone,
      };

      await store.dispatch("klaviyo/resendVerificationCode", params);

      toast.success("Verification code resent successfully!", {
        position: POSITION.TOP_LEFT,
      });
      disableButtonWithCountdown();
    } catch (error) {
      catchResendError(error);
    }
  };

  const disableButtonWithCountdown = () => {
    clearButtonDisabledInterval();

    isButtonDisabled.value = true;
    countdown.value = SET_INITIAL_COUNTDOWN_SECONDS;

    buttonDisabledInterval.value = setInterval(() => {
      if (countdown.value > 0) {
        countdown.value--;
      } else {
        clearButtonDisabledInterval();
        isButtonDisabled.value = false;
      }
    }, 1000);
  };

  const catchResendError = (error: unknown) => {
    let errorMessage = "Failed to resend verification code.";

    if (
      axios.isAxiosError(error) &&
      error.response?.data.message ===
        ErrorMessages.ERROR_WAIT_BEFORE_REQUESTING_NEW_CODE
    ) {
      errorMessage = ErrorMessages.ERROR_WAIT_BEFORE_REQUESTING_NEW_CODE;
    }

    toast.error(errorMessage, {
      position: POSITION.TOP_LEFT,
    });
  };

  const onVerify = async () => {
    const isValid = await validateForm();

    if (!isValid) {
      return;
    }

    try {
      isLoading.value = true;

      const normalizedPhone = parseAustralianNumberToInternational(form.phone);
      const validateDto: ValidatePhoneNumberDto = {
        email: form.email,
        phoneNumber: normalizedPhone,
        code: form.digitCode,
      };

      const response = await store.dispatch(
        "klaviyo/validatePhoneNumber",
        validateDto
      );

      await store.dispatch("builder/sendQuote", props.quote);
      emits("send");
      onClose();
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        toast.error("Invalid verfication code");
      } else if (error.response && error.response.status === 410) {
        toast.error("Code has expired. Please request a new code", {
          position: POSITION.TOP_LEFT,
        });
      } else {
        const { ERROR_VALIDATE_PHONE_NUMBER } = ErrorMessages;

        toast.error(ERROR_VALIDATE_PHONE_NUMBER, {
          position: POSITION.TOP_LEFT,
        });
      }
    } finally {
      isLoading.value = false;
    }
  };

  const validateForm = async () => {
    const isValid = await v$.value.$validate();

    v$.value.$touch();
    return isValid;
  };

  const onlyAllowNumbers = (event: KeyboardEvent) => {
    const key = event.key;

    if (!/[0-9]/.test(key) && key !== "Backspace" && key !== "Delete") {
      event.preventDefault();
    }
  };

  const clearButtonDisabledInterval = () => {
    if (buttonDisabledInterval.value) {
      clearInterval(buttonDisabledInterval.value);
      buttonDisabledInterval.value = null;
    }
  };

  onBeforeUnmount(() => {
    clearButtonDisabledInterval();
  });

  watch(
    () => props.isOpen,
    () => disableButtonWithCountdown()
  );

  watch(
    () => props.quote,
    () => {
      if (props.quote?.lead) {
        form.phone = props.quote?.lead?.phone;
        form.email = props.quote?.lead?.email;
      }
    }
  );

const __returned__ = { toast, emits, props, rules, formFields, form, v$, isLoading, SET_INITIAL_COUNTDOWN_SECONDS, isButtonDisabled, buttonDisabledInterval, countdown, resendButtonText, onClose, onResendCode, disableButtonWithCountdown, catchResendError, onVerify, validateForm, onlyAllowNumbers, clearButtonDisabledInterval, Modal, computed, onBeforeUnmount, reactive, ref, watch, CheckboxInput, get useVuelidate() { return useVuelidate }, get required() { return required }, get helpers() { return helpers }, get minLength() { return minLength }, get maxLength() { return maxLength }, get Cleave() { return Cleave }, get phoneNumberOption() { return phoneNumberOption }, get phonePlaceholder() { return phonePlaceholder }, get validPhoneNumber() { return validPhoneNumber }, get store() { return store }, get useToast() { return useToast }, get POSITION() { return POSITION }, get KlaviyoErrorEnum() { return KlaviyoErrorEnum }, get axios() { return axios }, get ErrorMessages() { return ErrorMessages }, get LeadForVerificationPhoneNumber() { return LeadForVerificationPhoneNumber }, get parseAustralianNumberToInternational() { return parseAustralianNumberToInternational }, get ValidatePhoneNumberDto() { return ValidatePhoneNumberDto }, get ResendCodeDto() { return ResendCodeDto } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})